import { useEffect, useState } from "react";
import { Link } from "wouter";

interface BlogPost {
  id: string;
  title: string;
  url: string;
  published: string;
  snippet: string;
  author?: string;
  image?: string;
  tags?: string[];
}

function extractImage(entry: any): string | undefined {
  // Try to extract image from media$thumbnail or content
  if (entry["media$thumbnail"]) {
    return entry["media$thumbnail"].url;
  }
  if (entry.content && entry.content.$t) {
    const match = entry.content.$t.match(/<img[^>]+src=["']([^"'>]+)["']/i);
    if (match) return match[1];
  }
  return undefined;
}

function extractTags(entry: any): string[] {
  if (!entry.category) return [];
  return entry.category.map((cat: any) => cat.term);
}

function getInternalPath(url: string): string | undefined {
  const match = url.match(/blog\.solanavolumebot\.io(\/\d{4}\/\d{2}\/[^/]+\.html)/);
  if (!match) {
    console.warn('Could not extract internal blog path from URL:', url);
    return undefined;
  }
  return `/blog${match[1]}`;
}

// Add fade-in animation CSS
const cardFadeIn = {
  animation: 'fadeInUp 0.7s cubic-bezier(0.23, 1, 0.32, 1) both',
};

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visiblePosts, setVisiblePosts] = useState(6); // Show 6 by default
  const POSTS_PER_PAGE = 6;

  // Detect language from URL (e.g., /ar/, /de/, etc.)
  function getCurrentLang() {
    const match = window.location.pathname.match(/^\/(\w{2})(\/|$)/);
    if (match && match[1] !== 'en') return match[1];
    return 'en';
  }

  useEffect(() => {
    async function fetchBlogPosts() {
      try {
        setLoading(true);
        setError(null);
        const lang = getCurrentLang();
        let feedUrl = '/blog-feed';
        if (lang !== 'en') feedUrl = `/blog-feed-${lang}.json`;
        let res = await fetch(feedUrl);
        // Fallback to English if translation file is missing
        if (!res.ok && lang !== 'en') {
          res = await fetch('/blog-feed');
        }
        if (!res.ok) throw new Error('Failed to fetch blog feed');
        let data;
        try {
          data = await res.json();
        } catch (jsonErr) {
          const text = await res.text();
          console.error('Blog feed is not valid JSON:', text);
          setError('Blog feed is not valid JSON. Check the console for details.');
          setLoading(false);
          return;
        }
        const entries = data.feed?.entry || [];
        const posts: BlogPost[] = entries.map((entry: any) => ({
          id: entry.id.$t,
          title: entry.title.$t,
          url:
            entry.link.find((l: any) => l.rel === 'alternate')?.href || '#',
          published: entry.published.$t.split('T')[0],
          snippet:
            entry.summary?.$t ||
            entry.content?.$t?.replace(/<[^>]+>/g, '').slice(0, 180) + '...' ||
            '',
          author: entry.author?.[0]?.name?.$t,
          image: extractImage(entry),
          tags: extractTags(entry),
        }));
        setPosts(posts);
      } catch (err: any) {
        console.error('Blog fetch error:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchBlogPosts();
  }, []);

  const hasPosts = posts.length > 0;
  const hasMultiple = posts.length > 1;
  const featured = hasPosts ? posts[0] : undefined;
  const rest = hasMultiple ? posts.slice(1) : [];
  const featuredInternalPath = featured ? getInternalPath(featured.url) : undefined;

  console.log('Featured post URL:', featured?.url);
  console.log('Featured internal path:', featuredInternalPath);

  return (
    <section className="py-20 min-h-screen bg-black/80">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-white text-center w-full md:w-auto">
            Blog & Updates
          </h1>
          <Link to="/" className="hidden md:inline-block px-5 py-2 rounded-lg bg-[#0A3D2B] border border-[#14F195]/40 text-white font-bold hover:bg-[#14F195]/20 transition-all ml-4">
            ← Back to Home
          </Link>
        </div>
        {/* Mobile back button */}
        <Link to="/" className="md:hidden block mb-6 px-5 py-2 rounded-lg bg-[#0A3D2B] border border-[#14F195]/40 text-white font-bold hover:bg-[#14F195]/20 transition-all w-full text-center">
          ← Back to Home
        </Link>
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse bg-[#18181b] rounded-xl p-6 h-64 border border-[#14F195]/30" />
            ))}
          </div>
        )}
        {error && (
          <div className="text-red-500 text-center py-10">{error}</div>
        )}
        {!loading && !error && hasPosts && hasMultiple && featured && (
          <>
            <div className="mb-12 flex justify-center">
              {featuredInternalPath && (
                <Link
                  to={featuredInternalPath}
                  style={cardFadeIn}
                  className="block w-full md:w-3/4 bg-gradient-to-br from-[#18181b]/90 to-[#23272e]/80 backdrop-blur-lg rounded-2xl shadow-2xl border-2 border-[#14F195]/30 hover:border-[#14F195] hover:shadow-[#14F195]/30 hover:-translate-y-1 transition-all overflow-hidden group"
                >
                  {featured.image && (
                    <img
                      src={featured.image}
                      alt={featured.title || "Blog post image"}
                      className="w-full h-64 object-cover object-center rounded-t-2xl group-hover:scale-105 transition-transform duration-300"
                    />
                  )}
                  <div className="flex-1 p-8 flex flex-col justify-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-2 group-hover:text-[#14F195] transition-colors">
                      {featured.title || "Untitled"}
                    </h2>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {featured.author && (
                        <span className="text-sm text-white font-semibold">
                          {featured.author}
                        </span>
                      )}
                      <span className="text-gray-200 text-xs">
                        {featured.published || ""}
                      </span>
                      {featured.tags?.map((tag) => (
                        <span key={tag} className="bg-[#0A3D2B] text-white text-xs px-3 py-1 rounded-full ml-2 font-bold shadow-sm">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <p className="text-gray-300 mb-4 text-lg">
                      {featured.snippet || ""}
                    </p>
                    <span className="inline-block mt-2 text-white font-bold group-hover:underline">
                      Read Full Post →
                    </span>
                  </div>
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.slice(0, visiblePosts).map((post, i) => {
                if (!post) return null;
                const internalPath = getInternalPath(post.url);
                if (!internalPath) return null;
                return (
                  <Link
                    key={post.id}
                    to={internalPath}
                    style={cardFadeIn}
                    className="block bg-gradient-to-br from-[#18181b]/90 to-[#23272e]/80 backdrop-blur-lg rounded-xl p-6 shadow-lg hover:shadow-2xl border-2 border-[#14F195]/20 hover:border-[#14F195] hover:-translate-y-1 transition-all h-full flex flex-col group"
                  >
                    {post.image && (
                      <img
                        src={post.image}
                        alt={post.title || "Blog post image"}
                        className="w-full h-40 object-cover object-center rounded-lg mb-4 group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    )}
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-[#14F195] transition-colors">
                      {post.title || "Untitled"}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      {post.author && (
                        <span className="text-xs text-white font-semibold">
                          {post.author}
                        </span>
                      )}
                      <span className="text-gray-200 text-xs">
                        {post.published || ""}
                      </span>
                      {post.tags?.map((tag) => (
                        <span key={tag} className="bg-[#0A3D2B] text-white text-xs px-2 py-1 rounded-full ml-2 font-bold shadow-sm">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <p className="text-gray-300 mb-4 flex-1">
                      {post.snippet || ""}
                    </p>
                    <span className="inline-block mt-auto text-white font-bold group-hover:underline">
                      Read More →
                    </span>
                  </Link>
                );
              })}
            </div>
            {visiblePosts < rest.length && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setVisiblePosts(prev => Math.min(prev + POSTS_PER_PAGE, rest.length))}
                  className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#00FFA3] to-[#DC1FFF] text-black font-bold hover:from-[#DC1FFF] hover:to-[#00FFA3] hover:text-white transition-colors duration-200"
                >
                  View More Posts
                </button>
              </div>
            )}
          </>
        )}
        {!loading && !error && hasPosts && !hasMultiple && featured && (
          <div className="flex justify-center">
            {featuredInternalPath ? (
              <Link
                to={featuredInternalPath}
                style={cardFadeIn}
                className="block w-full md:w-2/3 bg-gradient-to-br from-[#18181b]/90 to-[#23272e]/80 backdrop-blur-lg rounded-2xl shadow-2xl border-2 border-[#14F195]/30 hover:border-[#14F195] hover:shadow-[#14F195]/30 hover:-translate-y-1 transition-all flex flex-col group"
              >
                {featured.image && (
                  <img
                    src={featured.image}
                    alt={featured.title || "Blog post image"}
                    className="w-full h-56 object-cover object-center rounded-t-2xl group-hover:scale-105 transition-transform duration-300"
                  />
                )}
                <div className="flex-1 p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-extrabold text-white mb-2 group-hover:text-[#14F195] transition-colors">
                    {featured.title || "Untitled"}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    {featured.author && (
                      <span className="text-sm text-white font-semibold">
                        {featured.author}
                      </span>
                    )}
                    <span className="text-gray-200 text-xs">
                      {featured.published || ""}
                    </span>
                    {featured.tags?.map((tag) => (
                      <span key={tag} className="bg-[#0A3D2B] text-white text-xs px-3 py-1 rounded-full ml-2 font-bold shadow-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 text-lg">
                    {featured.snippet || ""}
                  </p>
                  <span className="inline-block mt-2 text-white font-bold group-hover:underline">
                    Read More →
                  </span>
                </div>
              </Link>
            ) : (
              <div className="block w-full md:w-2/3 bg-gradient-to-br from-[#18181b]/90 to-[#23272e]/80 backdrop-blur-lg rounded-2xl shadow-2xl border-2 border-[#14F195]/30 flex flex-col opacity-60 cursor-not-allowed p-8 mx-auto">
                <h3 className="text-3xl font-extrabold text-white mb-2">
                  {featured.title || "Untitled"}
                </h3>
                <div className="text-red-500 text-xs mt-2">No internal link generated for this post. Check console for details.</div>
              </div>
            )}
          </div>
        )}
        {!loading && !error && !hasPosts && (
          <div className="text-gray-200 text-center py-10">No blog posts found.</div>
        )}
      </div>
      {/* Fade-in animation keyframes */}
      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
} 